import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/Button';
import { MapPin, Clock, Phone, Mail, ShoppingBag, Sofa, Lightbulb, Frame } from 'lucide-react';
import { Link } from 'react-router-dom';
export function StoreInfo() {
  return <Layout data-id="element-674">
      {/* Hero Section */}
      <section className="relative py-32 px-4 border-b border-taupe-200 overflow-hidden" data-id="element-675">
        {/* Background Image */}
        <div className="absolute inset-0" data-id="element-676">
          <img src="https://cdn.magicpatterns.com/uploads/qvRoWqs2vhk3dGRoVgjotQ/Store_Info_bg.png" alt="Down Home Interiors Store Location" className="w-full h-full object-cover object-center" data-id="element-677" />
          <div className="absolute inset-0 bg-white/80" data-id="element-678" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-679">
          <span className="text-sage-600 uppercase tracking-[0.2em] text-xs font-semibold mb-6 block" data-id="element-680">
            Visit Us
          </span>
          <h1 className="text-4xl md:text-6xl font-serif text-charcoal-900 mb-6 leading-tight" data-id="element-681">
            Store Hours &<br data-id="element-682" />
            <span className="italic" data-id="element-683">Location</span>
          </h1>
          <p className="text-xl text-charcoal-900/70 leading-relaxed max-w-2xl mx-auto" data-id="element-684">
            Visit our retail store in Howell to explore our curated collection
            and meet with our design team.
          </p>
        </div>
      </section>

      {/* Visit Our Store */}
      <section className="py-24 px-4 bg-white" data-id="element-685">
        <div className="max-w-4xl mx-auto text-center" data-id="element-686">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-8" data-id="element-687">
            Your Destination for Inspired Living
          </h2>
          <p className="text-lg text-charcoal-900/70 leading-relaxed mb-6" data-id="element-688">
            Step into Down Home Interiors and discover a thoughtfully curated
            collection of home décor, apparel, and accessories. Our retail store
            is designed to inspire, with displays that showcase how pieces work
            together to create cohesive, livable spaces.
          </p>
          <p className="text-lg text-charcoal-900/70 leading-relaxed" data-id="element-689">
            Whether you're furnishing an entire home or looking for that perfect
            finishing touch, our team is here to help you find exactly what you
            need. We also offer design guidance right in the store—bring your
            room measurements, photos, or fabric samples and let us help you
            create a plan.
          </p>
        </div>
      </section>

      {/* Location & Hours */}
      <section className="py-24 px-4 bg-cream-100" data-id="element-690">
        <div className="max-w-6xl mx-auto" data-id="element-691">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-692">
            {/* Contact Information */}
            <div className="space-y-8" data-id="element-693">
              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-694">
                <div className="flex items-start gap-4 mb-6" data-id="element-695">
                  <MapPin className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-696" />
                  <div data-id="element-697">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-698">
                      Location
                    </h3>
                    <address className="not-italic text-lg text-charcoal-900/70 leading-relaxed" data-id="element-699">
                      <a href="https://www.google.com/maps/search/?api=1&query=2729+E.+Grand+River+Ave.+Howell+MI+48843" target="_blank" rel="noopener noreferrer" className="hover:text-sage-500 transition-colors" data-id="element-700">
                        2729 E. Grand River Ave.
                        <br data-id="element-701" />
                        Howell, MI 48843
                      </a>
                    </address>
                  </div>
                </div>
                <a href="https://www.google.com/maps/search/?api=1&query=2729+E.+Grand+River+Ave.+Howell+MI+48843" target="_blank" rel="noopener noreferrer" className="inline-block" data-id="element-702">
                  <Button variant="outline" size="sm" data-id="element-703">
                    Get Directions
                  </Button>
                </a>
              </div>

              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-704">
                <div className="flex items-start gap-4 mb-6" data-id="element-705">
                  <Clock className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-706" />
                  <div className="flex-1" data-id="element-707">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-4" data-id="element-708">
                      Store Hours
                    </h3>
                    <div className="space-y-3" data-id="element-709">
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-710">
                        <span className="text-charcoal-900/70" data-id="element-711">Sunday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-712">
                          10:30am – 3:30pm
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-713">
                        <span className="text-charcoal-900/70" data-id="element-714">Monday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-715">
                          10am – 4pm
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-716">
                        <span className="text-charcoal-900/70" data-id="element-717">Tue – Thu</span>
                        <span className="font-medium text-charcoal-900" data-id="element-718">
                          10am – 5:30pm
                        </span>
                      </div>
                      <div className="flex justify-between items-center pb-3 border-b border-taupe-100" data-id="element-719">
                        <span className="text-charcoal-900/70" data-id="element-720">Friday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-721">
                          10am – 5pm
                        </span>
                      </div>
                      <div className="flex justify-between items-center" data-id="element-722">
                        <span className="text-charcoal-900/70" data-id="element-723">Saturday</span>
                        <span className="font-medium text-charcoal-900" data-id="element-724">
                          Closed
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-charcoal-900/60 italic" data-id="element-725">
                  Hours may vary during holidays. Call ahead to confirm.
                </p>
              </div>

              <div className="bg-white p-8 border border-taupe-200 shadow-card" data-id="element-726">
                <div className="flex items-start gap-4" data-id="element-727">
                  <Phone className="text-sage-500 flex-shrink-0 mt-1" size={24} data-id="element-728" />
                  <div data-id="element-729">
                    <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-730">
                      Contact
                    </h3>
                    <div className="space-y-2 text-charcoal-900/70" data-id="element-731">
                      <p className="flex items-center gap-2" data-id="element-732">
                        <Phone size={16} data-id="element-733" />
                        <a href="tel:5175455955" className="hover:text-sage-500 transition-colors" data-id="element-734">
                          (517) 545-5955
                        </a>
                      </p>
                      <p className="flex items-center gap-2" data-id="element-735">
                        <Mail size={16} data-id="element-736" />
                        <a href="mailto:info@downhomeinteriors.com" className="hover:text-sage-500 transition-colors" data-id="element-737">
                          info@downhomeinteriors.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <div className="h-full min-h-[600px] border border-taupe-200 shadow-card overflow-hidden" data-id="element-738">
              <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2937.047240191924!2d-83.90287152328551!3d42.59674122011513!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8823475fb0e59093%3A0x1164ffa174cf8cad!2sDown%20Home%20Interiors!5e0!3m2!1sen!2sus!4v1768330470795!5m2!1sen!2sus" width="100%" height="100%" style={{
              border: 0
            }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Down Home Interiors Location" data-id="element-739" />
            </div>
          </div>
        </div>
      </section>

      {/* What You'll Find In-Store */}
      <section className="py-24 px-4 bg-white" data-id="element-740">
        <div className="max-w-6xl mx-auto" data-id="element-741">
          <div className="text-center mb-16" data-id="element-742">
            <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-4" data-id="element-743">
              What You'll Find In-Store
            </h2>
            <p className="text-lg text-charcoal-900/60 max-w-2xl mx-auto" data-id="element-744">
              A carefully curated selection for every room in your home
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-745">
            {[{
            icon: <Sofa className="text-sage-500" size={32} data-id="element-746" />,
            title: 'Furniture',
            description: 'Sofas, chairs, tables, and bedroom sets in classic and contemporary styles'
          }, {
            icon: <Lightbulb className="text-sage-500" size={32} data-id="element-747" />,
            title: 'Lighting',
            description: 'Table lamps, floor lamps, chandeliers, and decorative fixtures'
          }, {
            icon: <Frame className="text-sage-500" size={32} data-id="element-748" />,
            title: 'Décor & Art',
            description: 'Wall art, mirrors, vases, and accessories to complete your space'
          }, {
            icon: <ShoppingBag className="text-sage-500" size={32} data-id="element-749" />,
            title: 'Apparel & Gifts',
            description: 'Boutique clothing, jewelry, and unique gift items'
          }].map((category, index) => <div key={index} className="text-center" data-id="element-750">
                <div className="w-20 h-20 bg-sage-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-751">
                  {category.icon}
                </div>
                <h3 className="text-xl font-serif text-charcoal-900 mb-3" data-id="element-752">
                  {category.title}
                </h3>
                <p className="text-charcoal-900/70 leading-relaxed" data-id="element-753">
                  {category.description}
                </p>
              </div>)}
          </div>

          <div className="mt-16 bg-cream-50 p-8 md:p-12 border border-taupe-200 text-center" data-id="element-754">
            <h3 className="text-2xl font-serif text-charcoal-900 mb-4" data-id="element-755">
              Can't Find What You're Looking For?
            </h3>
            <p className="text-lg text-charcoal-900/70 mb-6 max-w-2xl mx-auto" data-id="element-756">
              Our retail store represents just a fraction of what we can source
              for you. With access to hundreds of manufacturers and
              craftspeople, we can help you find or create exactly what you
              need.
            </p>
            <a href="tel:5175455955" data-id="element-757">
              <Button data-id="element-758">Call to Get Started</Button>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-gradient-to-br from-taupe-200/50 to-cream-100" data-id="element-759">
        <div className="max-w-4xl mx-auto text-center" data-id="element-760">
          <h2 className="text-3xl md:text-4xl font-serif text-charcoal-900 mb-6" data-id="element-761">
            We'd Love to See You
          </h2>
          <p className="text-xl text-charcoal-900/70 mb-10 leading-relaxed max-w-2xl mx-auto" data-id="element-762">
            Stop by during our regular hours or call us to discuss your project.
          </p>
          <a href="tel:5175455955" data-id="element-763">
            <Button size="lg" data-id="element-764">Call Us Today</Button>
          </a>
        </div>
      </section>
    </Layout>;
}